export type LeadUser = {
  id: number;
  email: string;
  username: string;
};

export type LeadCategoryRef = {
  id: number;
  name: string;
  slug: string;
};

export type LeadCategoryType = {
  id: number;
  name: string;
  slug: string;
  description: string;
  is_active: boolean;
  lead_count?: number;
};

export type LeadType = {
  id: number;
  category: LeadCategoryRef | null;
  company_name: string;
  company_linkedin_url: string | null;
  company_website_url: string | null;
  company_email: string | null;
  phone: string | null;
  google_place_id: string | null;
  google_maps_url: string | null;
  company_description: string;
  team_size_notes: string;
  website_notes: string;
  social_media_notes: string;
  contact_page_url: string | null;
  outreach_method: string;
  outreach_done: boolean;
  outreach_date: string | null;
  decision_maker_name: string;
  decision_maker_title: string;
  decision_maker_linkedin_url: string | null;
  decision_maker_email: string | null;
  status: string;
  priority: number | null;
  notes: string;
  created_by: LeadUser | null;
  assigned_to: LeadUser | null;
  is_from_research: boolean;
  created_at: string;
  updated_at: string;
};

export type LeadsPagination = {
  current_page: number;
  total_pages: number;
  total_count: number;
  has_next: boolean;
  has_previous: boolean;
  per_page: number;
};

export type LeadsQueryParams = {
  page?: number;
  per_page?: number;
  status?: string;
  search?: string;
  category_id?: number | string;
  sort?: string;
  // true = only research-sourced leads, false = only manually-added ones,
  // omit for both (pair with sort: 'source' to group research first).
  is_from_research?: boolean;
  priority?: number;
  has_website?: boolean;
  has_contact_email?: boolean;
  assigned_to_id?: number;
};

// Fields the add/edit lead form submits. category_id/status/outreach_method
// are the raw values the backend expects on write (unlike LeadType's nested
// `category`, which is what it returns on read).
export type LeadFormFields = {
  category_id: string;
  company_name: string;
  company_linkedin_url: string;
  company_website_url: string;
  company_email: string;
  phone: string;
  google_place_id: string;
  google_maps_url: string;
  company_description: string;
  team_size_notes: string;
  website_notes: string;
  social_media_notes: string;
  contact_page_url: string;
  outreach_method: string;
  outreach_done: boolean;
  decision_maker_name: string;
  decision_maker_title: string;
  decision_maker_linkedin_url: string;
  decision_maker_email: string;
  status: string;
  priority: string;
  notes: string;
};

// Priority is a plain 0-5 integer (higher = more important); no labels on
// the backend, so the option list is just the numbers themselves.
export const PRIORITY_OPTIONS: string[] = ['0', '1', '2', '3', '4', '5'];

// Single source of truth for lead status values/labels — was duplicated
// (with real risk of drifting) across OutreachView, ComposeView, ReportView,
// CommunicationsView, and LeadFormView. STATUS_LABELS is derived from this
// array rather than hand-duplicated, so the two shapes can't disagree.
export const LEAD_STATUS_OPTIONS: [string, string][] = [
  ['prefetch', 'Prefetching Info'],
  ['new', 'New'],
  ['contacted', 'Contacted'],
  ['follow_up', 'Needs Follow-up'],
  ['replied', 'Replied'],
  ['converted', 'Converted'],
  ['phonelist', 'Phone only'],
  ['disregard', 'Disregard'],
  ['rejected', 'Not Interested'],
];

export const STATUS_LABELS: Record<string, string> = Object.fromEntries(LEAD_STATUS_OPTIONS);

export const emptyLeadFormFields: LeadFormFields = {
  category_id: '',
  company_name: '',
  company_linkedin_url: '',
  company_website_url: '',
  company_email: '',
  phone: '',
  google_place_id: '',
  google_maps_url: '',
  company_description: '',
  team_size_notes: '',
  website_notes: '',
  social_media_notes: '',
  contact_page_url: '',
  outreach_method: '',
  outreach_done: false,
  decision_maker_name: '',
  decision_maker_title: '',
  decision_maker_linkedin_url: '',
  decision_maker_email: '',
  status: 'new',
  priority: '',
  notes: '',
};
